// src/components/admin/AdminLayout.tsx
import React, { useState } from 'react';
import { Shield, LayoutDashboard, Users, BarChart3, LogOut, DollarSign, Database } from 'lucide-react';
import SystemMonitor from './SystemMonitor';
import AccountManagement from './AccountManagement'; 
import { User } from '../../types';

// 👇 새로 만든 수익률 분석 컴포넌트 불러오기
import ProfitAnalysis from './ProfitAnalysis';
//나라장터 입찰 공고
import G2BAdminBoard from './G2BAdminBoard';


interface AdminLayoutProps {
  onBackToMain: () => void;
  currentUser: User | null;
  globalRefreshKey?: number;
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ 
  onBackToMain, 
  currentUser,
  globalRefreshKey = 0
}) => {
  // 탭 상태 관리
  const [activeTab, setActiveTab] = useState<'system' | 'account' | 'profit' | 'audit' | 'g2b' > ('system');

  return (
    <div className="flex flex-col h-screen bg-slate-100 font-sans overflow-hidden">
      
      {/* 🟢 [제일 상단] 통합 헤더 & 탭바 */}
      <header className="bg-white h-14 border-b border-slate-200 flex items-center px-6 justify-between shadow-sm flex-none">
        <div className="flex items-center gap-10">
          <div className="flex items-center gap-2 border-r border-slate-100 pr-6">
            <Shield className="text-blue-600" size={20} />
            <span className="font-black text-slate-800 tracking-tight">Admin Mode</span>
          </div>

          {/* 탭 버튼들 */}
          <nav className="flex items-center gap-2">
            {[
              { id: 'system', label: '시스템 모니터링', icon: <LayoutDashboard size={14} /> },
              { id: 'account', label: '계정 관리', icon: <Users size={14} /> },
              { id: 'audit', label: '영업 품질 관리', icon: <BarChart3 size={14} /> },
              // 👇 [추가] 여기에 수익률 분석 탭 추가
              { id: 'profit', label: '프로젝트 손익 관리', icon: <DollarSign size={14} /> },

              // 🚨 2. 나라장터 수집관리 탭 메뉴 추가
              { id: 'g2b', label: '나라장터 수집관리', icon: <Database size={14} /> }

            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`
                  flex items-center gap-2 px-4 py-1.5 rounded-lg text-xs font-black transition-all
                  ${activeTab === tab.id 
                    ? 'bg-slate-900 text-white shadow-md' 
                    : 'text-slate-400 hover:bg-slate-50 hover:text-slate-600'
                  }
                `}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* 🚨 상단에 로그인한 사용자 이름 표시 (관리자 확인용) */}
        <div className="flex items-center gap-4">
          <span className="text-[10px] font-black text-slate-400 uppercase tracking-widest border-r pr-4 border-slate-100">
             Log in as: <span className="text-blue-600">{currentUser?.name}</span>
          </span>
          <button 
            onClick={onBackToMain}
            className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-slate-200 text-slate-500 hover:bg-red-50 hover:text-red-600 hover:border-red-100 transition-all text-xs font-bold"
          >
            <LogOut size={14} />
            나가기
          </button>
        </div>
      </header>

      {/* 🔵 메인 콘텐츠 영역 */}
      <main className="flex-1 p-6 overflow-hidden">
        {/* 탭에 따라 컴포넌트 교체 */}
        {activeTab === 'system' && (
          <SystemMonitor globalRefreshKey={globalRefreshKey} />
        )}
        
        {activeTab === 'account' && (
          <AccountManagement globalRefreshKey={globalRefreshKey} />
        )}

        {activeTab === 'audit' && (
          <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-12 h-full flex flex-col items-center justify-center">
             <BarChart3 size={48} className="text-slate-200 mb-4" />
             <h2 className="text-xl font-black text-slate-800 mb-1">영업 품질 관리</h2>
             <p className="text-slate-400 text-sm">임원용 장기 미방문 리스트 준비 중</p>
          </div>
        )}

        {/* 3. 💰 [추가] 프로젝트 손익 관리 */}
        {activeTab === 'profit' && (
          <div className="h-full overflow-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-4">
            <ProfitAnalysis globalRefreshKey={globalRefreshKey} />
          </div>
        )}

        {/* 4. 'g2b' 탭일 때 우리가 만든 보드 보여주기 */}
        {activeTab === 'g2b' && (
          <div className="h-full overflow-hidden">
             <G2BAdminBoard />
          </div>
        )}
        
      </main>
    </div>
  );
};

export default AdminLayout;
